import React from 'react';
import { Segment, ActionType, ActionAnnotation } from './types';

// Component for individual segment editing
interface SegmentEditorProps {
    segment: Segment;
    onUpdate: (segmentIndex: number, annotation: ActionAnnotation) => void;
    onDelete: (segmentIndex: number) => void;
    onSetCurrentTime: (time: number) => void;
    currentTime: number;
}

const SegmentEditor: React.FC<SegmentEditorProps> = ({ 
    segment, 
    onUpdate, 
    onDelete, 
    onSetCurrentTime,
    currentTime 
}) => {
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = (seconds % 60).toFixed(1);
        return `${mins}:${secs.padStart(4, '0')}`;
    };

    const handleActionChange = (action: ActionType) => {
        onUpdate(segment.index, { 
            action, 
            comment: segment.annotation.comment 
        });
    };

    const handleCommentChange = (comment: string) => {
        onUpdate(segment.index, { 
            action: segment.annotation.action, 
            comment 
        });
    };

    const jumpToSegmentStart = () => {
        onSetCurrentTime(segment.beginTime);
    };


    const isCurrentlyInSegment = currentTime >= segment.beginTime && currentTime <= segment.endTime;

    return (
        <div style={{ 
            border: isCurrentlyInSegment ? '2px solid #007bff' : '1px solid #ccc', 
            padding: '15px', 
            margin: '5px 0', 
            borderRadius: '5px',
            backgroundColor: isCurrentlyInSegment ? '#f0f8ff' : '#f9f9f9'
        }}>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 'bold', minWidth: '80px' }}>
                    Segment #{segment.index+1}
                </span>
                
                <span style={{ color: '#666' }}>
                    {formatTime(segment.beginTime)} - {formatTime(segment.endTime)}
                </span>
                
                <button 
                    type="button"
                    onClick={jumpToSegmentStart}
                    style={{ 
                        backgroundColor: '#28a745', 
                        color: 'white', 
                        border: 'none', 
                        padding: '4px 8px',
                        borderRadius: '3px',
                        fontSize: '12px',
                        cursor: 'pointer'
                    }}
                >
                    Go to Start
                </button>

                {segment.index > 0 && (
                    <button 
                        type="button"
                        onClick={() => onDelete(segment.index)}
                        style={{ 
                            backgroundColor: '#dc3545', 
                            color: 'white', 
                            border: 'none', 
                            padding: '4px 8px',
                            borderRadius: '3px',
                            fontSize: '12px',
                            cursor: 'pointer'
                        }}
                    >
                        Delete
                    </button>
                )}
            </div>

            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    Action:
                    <select
                        value={segment.annotation.action}
                        onChange={(e) => handleActionChange(e.target.value as ActionType)}
                        style={{ 
                            padding: '4px 8px',
                            borderRadius: '3px',
                            border: '1px solid #ccc'
                        }}
                    >
                        {Object.values(ActionType).map(actionType => (
                            <option key={actionType} value={actionType}>
                                {actionType}
                            </option>
                        ))}
                    </select>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: 1 }}>
                    Comment:
                    <input
                        type="text"
                        value={segment.annotation.comment || ''}
                        onChange={(e) => handleCommentChange(e.target.value)}
                        placeholder="Optional comment..."
                        style={{ 
                            padding: '4px 8px',
                            borderRadius: '3px',
                            border: '1px solid #ccc',
                            flex: 1,
                            minWidth: '200px'
                        }}
                    />
                </label>
            </div>
        </div>
    );
};

export default SegmentEditor;